import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, viewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { BasemapTileComponent } from '@connect-services/client/components/basemap-tile/basemap-tile.component';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { BasemapManagementModalPickerComponent } from '@connect-services/client/components/modals/basemap-management-modal/basemap-management-modal-picker/basemap-management-modal-picker.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import {
    ALL_BASEMAP_TYPES,
    BasemapInfo,
    BASEMAPS_CATALOG,
    BasemapSecondary,
    BasemapService,
    DEFAULT_SECONDARY_OPACITY,
    TileSourceTypeEnum,
} from '@connect-services/client/services/basemap.service';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { ToggleComponent } from '@shared/client/components/toggle/toggle.component';

export enum PickerMode {
    PRIMARY = 'PRIMARY',
    SECONDARY_ADD = 'SECONDARY_ADD',
    SECONDARY_REPLACE = 'SECONDARY_REPLACE',
}

export type PickerContext =
    | { mode: PickerMode.PRIMARY }
    | { mode: PickerMode.SECONDARY_ADD }
    | { mode: PickerMode.SECONDARY_REPLACE; secondaryTypeToReplace: TileSourceTypeEnum }
    | null;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BasemapManagementModalPickerComponent,
        BasemapTileComponent,
        ComplexModalTemplate,
        RessourcesModule,
        ToggleComponent,
    ],
    selector: 'app-basemap-management-modal',
    styleUrl: './basemap-management-modal.component.scss',
    templateUrl: './basemap-management-modal.component.html',
})
export class BasemapManagementModalComponent {
    public readonly basemapService = inject(BasemapService);
    public readonly primaryBasemap = this.basemapService.primary;
    public readonly secondariesBasemap = this.basemapService.secondaries;

    protected readonly ALL_BASEMAP_TYPES: readonly TileSourceTypeEnum[] = ALL_BASEMAP_TYPES;
    protected readonly catalog: Readonly<Record<TileSourceTypeEnum, BasemapInfo>> = BASEMAPS_CATALOG;
    protected readonly eligibleTypesForAdd = computed<TileSourceTypeEnum[]>(() => {
        const taken = new Set<TileSourceTypeEnum>([
            this.primaryBasemap(),
            ...this.secondariesBasemap().map((basemapSecondary: BasemapSecondary) => basemapSecondary.type),
        ]);

        return ALL_BASEMAP_TYPES.filter((tile: TileSourceTypeEnum) => !taken.has(tile));
    });
    protected readonly hoveredType = signal<TileSourceTypeEnum | null>(null);
    protected readonly isPrimaryOpen = computed(() => this.picker()?.mode === PickerMode.PRIMARY);
    protected readonly isSecondaryOpen = computed(() => this.picker()?.mode === PickerMode.SECONDARY_REPLACE);
    protected readonly picker = signal<PickerContext>(null);
    protected readonly pickerLeft = signal<number>(0);
    protected readonly PickerMode = PickerMode;
    protected readonly replacingSecondary = computed<TileSourceTypeEnum | null>(() => {
        const picker = this.picker();

        return picker?.mode === PickerMode.SECONDARY_REPLACE ? picker.secondaryTypeToReplace : null;
    });
    protected readonly showAddButton = computed<boolean>(() => {
        return !!this.eligibleTypesForAdd().length;
    });

    private readonly basemapDialogRef = inject(MatDialogRef<BasemapManagementModalComponent>);
    private readonly modalContainer = viewChild<ElementRef<HTMLDivElement>>('modalRoot');

    public closeModal(modalResult: ModalResultEnum): void {
        this.basemapService.isActive.set(false);
        this.basemapService.setButtonIcon();
        this.basemapDialogRef.close(modalResult);
    }

    protected closePicker(): void {
        this.picker.set(null);
        this.hoveredType.set(null);
        this.pickerLeft.set(0);
    }

    protected openPickerAddSecondary(): void {
        if (!this.eligibleTypesForAdd().length) {
            return;
        }
        this.togglePicker({ mode: PickerMode.SECONDARY_ADD });
    }

    protected pickBasemapType(type: TileSourceTypeEnum): void {
        const pickerContext = this.picker();
        if (!pickerContext) {
            return;
        }

        let previous = null;
        let prevOpacity = DEFAULT_SECONDARY_OPACITY;

        switch (pickerContext.mode) {
            case PickerMode.PRIMARY:
                if (type !== this.primaryBasemap()) {
                    this.basemapService.selectPrimary(type);
                }
                break;
            case PickerMode.SECONDARY_ADD:
                this.basemapService.addSecondary(type);
                this.basemapService.setSecondaryOpacity(type, DEFAULT_SECONDARY_OPACITY);
                break;
            case PickerMode.SECONDARY_REPLACE:
                previous = this.secondariesBasemap().find(
                    (basemapSecondary: BasemapSecondary) =>
                        basemapSecondary.type === pickerContext.secondaryTypeToReplace,
                );
                prevOpacity = previous?.opacity ?? DEFAULT_SECONDARY_OPACITY;

                this.basemapService.changeSecondaryType(pickerContext.secondaryTypeToReplace, type);
                this.basemapService.setSecondaryOpacity(type, prevOpacity);

                break;
        }
        this.closePicker();
    }

    protected removeSecondary(type: TileSourceTypeEnum): void {
        this.basemapService.removeSecondary(type);
        this.closePicker();
    }

    protected togglePicker(nextPicker: PickerContext): void {
        const currentPicker = this.picker();
        const same = this.areSamePicker(currentPicker, nextPicker);

        if (same || !nextPicker) {
            this.closePicker();

            return;
        }

        this.picker.set(nextPicker);

        if (nextPicker.mode === PickerMode.PRIMARY) {
            requestAnimationFrame(() => this.computePickerLeft(PickerMode.PRIMARY));
        }
    }

    protected toggleTerrainElevation(isChecked: boolean): void {
        this.basemapService.toggleTerrainElevation(isChecked);
    }

    private areSamePicker(firstPicker: PickerContext, secondaryPicker: PickerContext): boolean {
        if (!firstPicker || firstPicker.mode !== secondaryPicker?.mode) {
            return false;
        }

        if (
            firstPicker.mode === PickerMode.SECONDARY_REPLACE &&
            secondaryPicker.mode === PickerMode.SECONDARY_REPLACE
        ) {
            return firstPicker.secondaryTypeToReplace === secondaryPicker.secondaryTypeToReplace;
        }

        return true;
    }

    private computePickerLeft(mode: PickerMode): void {
        const nativeElement: HTMLElement | undefined = this.modalContainer()?.nativeElement;
        if (!nativeElement) {
            return;
        }

        const containerDimensions: DOMRect = nativeElement.getBoundingClientRect();
        if (!containerDimensions) {
            return;
        }

        // "guardrail" in pixels to prevent the picker from touching the edges of the viewport, or overflowing.
        const MARGIN = 8;
        const PICKER_WIDTH = 320;

        let left =
            mode === PickerMode.PRIMARY
                ? containerDimensions.left
                : containerDimensions.left + containerDimensions.width / 2 - PICKER_WIDTH / 2;

        left = Math.max(MARGIN, Math.min(left, window.innerWidth - PICKER_WIDTH - MARGIN));
        this.pickerLeft.set(Math.round(left));
    }
}
